import type { CodeSessionRewindEventInput } from '@/lib/code-sessions/types';

import type { CodeSessionRewindAction } from './mutations';
import type { CodeSessionRow, CodeSessionTurn } from './types';

/** The row fields a rewind changes. */
type RewindRow = Pick<CodeSessionRow, 'rewindTurnId' | 'rewindRestoreFiles'> & {
  turns: readonly Pick<CodeSessionTurn, 'id' | 'index' | 'discardedAt'>[];
};

/** Mirror the server's commit: the boundary turn and every later one are discarded. */
function discardFromBoundary<R extends RewindRow>(
  session: R,
  boundaryIndex: number,
  keepTurnId?: string,
): R {
  const discardedAt = new Date().toISOString();
  return {
    ...session,
    rewindTurnId: null,
    rewindRestoreFiles: true,
    turns: session.turns.map((turn) =>
      turn.index >= boundaryIndex && turn.id !== keepTurnId && !turn.discardedAt
        ? { ...turn, discardedAt }
        : turn,
    ),
  };
}

/**
 * Apply a live rewind event to the cached row until a refetch confirms it. A
 * commit discards turns only while the row still waits on that rewind: a row
 * that has moved on may already hold the turn that replaced them.
 */
export function applyRewindEvent<R extends RewindRow>(
  session: R,
  event: CodeSessionRewindEventInput,
): R {
  switch (event.state) {
    case 'staged':
      return event.turnId
        ? {
            ...session,
            rewindTurnId: event.turnId,
            rewindRestoreFiles: event.restoreFiles ?? session.rewindRestoreFiles,
          }
        : session;
    case 'cleared':
      return { ...session, rewindTurnId: null, rewindRestoreFiles: true };
    case 'committed': {
      if (!event.turnId || session.rewindTurnId !== event.turnId) {
        return session;
      }
      const boundaryIndex =
        event.boundaryIndex ?? session.turns.find((turn) => turn.id === event.turnId)?.index;
      return boundaryIndex === undefined
        ? { ...session, rewindTurnId: null, rewindRestoreFiles: true }
        : discardFromBoundary(session, boundaryIndex);
    }
    default: {
      const _exhaustive: never = event;
      return _exhaustive;
    }
  }
}

/**
 * The row as the rewind request in flight will leave it, through the same
 * reducer as its event: the transcript shows the outcome at once, and a
 * failed request just stops applying.
 */
export function applyRewindAction<R extends RewindRow>(
  session: R,
  action: CodeSessionRewindAction | undefined,
): R {
  if (action === undefined) {
    return session;
  }
  switch (action.type) {
    case 'stage':
      return applyRewindEvent(session, {
        kind: 'rewind',
        state: 'staged',
        turnId: action.turnId,
        restoreFiles: action.restoreFiles,
      });
    case 'clear':
      return session.rewindTurnId === null
        ? session
        : applyRewindEvent(session, {
            kind: 'rewind',
            state: 'cleared',
            turnId: session.rewindTurnId,
          });
    default: {
      const _exhaustive: never = action;
      return _exhaustive;
    }
  }
}

/**
 * The server commits a waiting rewind before it creates the next turn, so a
 * newly saved turn after the boundary proves the commit even when its event
 * has not arrived yet.
 */
export function commitRewindForNewTurn<R extends RewindRow>(
  session: R,
  turn: Pick<CodeSessionTurn, 'id' | 'index'>,
): R {
  const boundary = session.rewindTurnId
    ? session.turns.find((saved) => saved.id === session.rewindTurnId)
    : undefined;
  return boundary && turn.index > boundary.index
    ? discardFromBoundary(session, boundary.index, turn.id)
    : session;
}
